"use client";

import {
  Children,
  isValidElement,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type HTMLAttributes,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";
import { createPortal } from "react-dom";
import { t, type Locale } from "@/lib/i18n";

function readLocale(): Locale {
  if (typeof window === "undefined") return "zh-Hant";
  const stored = window.localStorage.getItem("kairos.locale");
  if (stored === "zh-Hans" || stored === "en" || stored === "zh-Hant") return stored;
  return "zh-Hant";
}

export function EyeRevealIcon({ hidden }: { hidden: boolean }) {
  if (hidden) {
    return (
      <svg viewBox="0 0 24 24" fill="none" aria-hidden className="h-full w-full">
        <path
          d="M4 12c0 0 2.2-4.5 8-4.5s8 4.5 8 4.5"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
        <path d="M7.5 12.5v2M10 13.2v1.5M12 13.5v1.2M14 13.2v1.5M16.5 12.5v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden className="h-full w-full">
      <path
        d="M4 12s2.5-5 8-5 8 5 8 5-2.5 5-8 5-8-5-8-5z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="2.5" fill="currentColor" />
    </svg>
  );
}

export function cn(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

export function Logo({
  compact = false,
  markOnly = false,
}: {
  compact?: boolean;
  markOnly?: boolean;
}) {
  return (
    <span className="flex items-center gap-3">
      <span
        className={cn(
          "grid place-items-center rounded-xl bg-gradient-to-br from-[#f6c45a] to-[#e8a52e] text-white shadow-[0_8px_18px_var(--gold-glow)]",
          compact ? "h-8 w-8" : "h-10 w-10",
        )}
      >
        <span className="h-2.5 w-2.5 rotate-45 rounded-[2px] bg-white" />
      </span>
      {!markOnly && <span className={cn("font-bold text-[var(--text)]", compact ? "text-sm" : "text-xl")}>Kairos</span>}
    </span>
  );
}

export function Panel({
  children,
  className = "",
  ...rest
}: HTMLAttributes<HTMLDivElement> & { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "rounded-[24px] border border-[var(--border)] bg-[var(--surface)] shadow-[var(--shadow)] backdrop-blur",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export function Label({ children }: { children: ReactNode }) {
  return <label className="mb-1.5 block text-[11px] font-semibold text-slate-400">{children}</label>;
}

function captionMark(text: string, required?: boolean) {
  if (!required || text.trim().endsWith("*")) return text;
  return `${text} *`;
}

export function Input({
  label,
  className,
  onFocus,
  onBlur,
  onChange,
  placeholder,
  type,
  revealable,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label?: string; revealable?: boolean }) {
  const caption = label || placeholder;
  const [focused, setFocused] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [inner, setInner] = useState(() => String(props.defaultValue ?? ""));
  const current = props.value !== undefined ? String(props.value ?? "") : inner;
  const stacked = Boolean(caption) && (focused || current.length > 0);
  const canReveal = revealable ?? type === "password";
  const inputType = canReveal && revealed ? "text" : type;
  const locale = readLocale();
  const masked = canReveal && !revealed;

  return (
    <label
      className={cn(
        "field",
        caption && "field-labeled",
        stacked && "field--filled",
        canReveal && "field--reveal",
        className,
      )}
      data-disabled={props.disabled || undefined}
      suppressHydrationWarning
    >
      {caption ? <span className="field-caption">{captionMark(caption, props.required)}</span> : null}
      <input
        {...props}
        type={inputType}
        className="field-control"
        placeholder={caption ? undefined : placeholder}
        suppressHydrationWarning
        onFocus={(event) => {
          setFocused(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event);
        }}
        onChange={(event) => {
          if (props.value === undefined) setInner(event.target.value);
          onChange?.(event);
        }}
      />
      {canReveal ? (
        <button
          type="button"
          className="field-reveal"
          aria-label={masked ? t(locale, "showPassword") : t(locale, "hidePassword")}
          aria-pressed={!masked}
          disabled={props.disabled}
          tabIndex={-1}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => setRevealed((open) => !open)}
        >
          <EyeRevealIcon hidden={masked} />
        </button>
      ) : null}
    </label>
  );
}

export type SelectOption = { value: string; label: string; leading?: ReactNode };

function optionText(node: ReactNode): string {
  return Children.toArray(node)
    .map((child) => {
      if (typeof child === "string" || typeof child === "number") return String(child);
      if (isValidElement<{ children?: ReactNode }>(child)) return optionText(child.props.children);
      return "";
    })
    .join("")
    .replace(/\s+/g, " ")
    .trim();
}

function readSelectOptions(children: ReactNode, extra?: SelectOption[]): SelectOption[] {
  if (extra?.length) return extra;
  const items: SelectOption[] = [];
  Children.forEach(children, (child) => {
    if (!isValidElement<{ value?: string | number; children?: ReactNode }>(child)) return;
    if (child.type !== "option") return;
    const value = String(child.props.value ?? "");
    items.push({ value, label: optionText(child.props.children) || value });
  });
  return items;
}

function SelectOptionRow({ item, clip = true }: { item: SelectOption; clip?: boolean }) {
  if (!item.leading) return <>{item.label}</>;
  return (
    <span className="dd-item-row">
      {item.leading}
      <span className={clip ? "min-w-0 truncate" : "whitespace-nowrap"}>{item.label}</span>
    </span>
  );
}

export function Select({
  value,
  onChange,
  children,
  options,
  label,
  required,
  disabled,
  className,
  variant = "field",
  placeholder,
  name,
  clearable,
}: {
  value: string;
  onChange: (event: { target: { value: string } }) => void;
  children?: ReactNode;
  options?: SelectOption[];
  label?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  variant?: "field" | "pill" | "toolbar";
  placeholder?: string;
  name?: string;
  clearable?: boolean;
}) {
  const items = readSelectOptions(children, options);
  const selected = items.find((item) => item.value === value);
  const display =
    variant === "toolbar" && !value && placeholder
      ? placeholder
      : selected?.label || placeholder || "";
  const [open, setOpen] = useState(false);
  const stacked = Boolean(label) && (Boolean(selected) || open);
  const [active, setActive] = useState(value);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const place = () => {
      const rect = triggerRef.current!.getBoundingClientRect();
      const rich = items.some((item) => item.leading);
      const floor =
        variant === "pill" || variant === "toolbar"
          ? Math.max(rect.width, 132)
          : Math.max(rect.width, rich ? 420 : 220);
      const estimated = items.length * 44 + 16;
      const spaceBelow = window.innerHeight - rect.bottom - 12;
      const openUp = spaceBelow < Math.min(estimated, 220) && rect.top > spaceBelow;
      const menuWidth = menuRef.current?.getBoundingClientRect().width || floor;
      const width = Math.min(Math.max(menuWidth, floor), window.innerWidth - 16);
      const left = Math.max(
        8,
        Math.min(
          variant === "pill" || variant === "toolbar" ? rect.right - width : rect.left,
          window.innerWidth - width - 8,
        ),
      );
      setMenuStyle({
        position: "fixed",
        left,
        minWidth: floor,
        width: "max-content",
        maxWidth: window.innerWidth - 16,
        top: openUp ? undefined : rect.bottom + 6,
        bottom: openUp ? window.innerHeight - rect.top + 6 : undefined,
        zIndex: 110,
      });
    };
    place();
    const frame = window.requestAnimationFrame(place);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, items.length, variant]);

  useEffect(() => {
    if (!open) return;
    setActive(value);
    const onPointer = (event: MouseEvent) => {
      const node = event.target as Node;
      if (rootRef.current?.contains(node) || menuRef.current?.contains(node)) return;
      setOpen(false);
    };
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, value]);

  function pick(next: string) {
    onChange({ target: { value: next } });
    setOpen(false);
    triggerRef.current?.focus();
  }

  function onTriggerKey(event: KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;
    if (!open && (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ")) {
      event.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    const index = Math.max(0, items.findIndex((item) => item.value === active));
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive(items[Math.max(0, index - 1)]?.value ?? active);
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive(items[Math.min(items.length - 1, index + 1)]?.value ?? active);
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      pick(active || value);
    }
  }

  const menu = open
    ? createPortal(
        <div
          ref={menuRef}
          id={listId}
          role="listbox"
          className={cn("dd-menu", variant === "pill" && "dd-menu--center")}
          style={menuStyle}
        >
          {items.map((item, index) => (
            <button
              key={item.value || index}
              type="button"
              role="option"
              aria-selected={item.value === value}
              className={cn("dd-item", item.leading && "dd-item--face")}
              data-active={item.value === active || undefined}
              data-selected={item.value === value || undefined}
              onMouseEnter={() => setActive(item.value)}
              onClick={() => pick(item.value)}
            >
              <SelectOptionRow item={item} clip={false} />
            </button>
          ))}
        </div>,
        document.body,
      )
    : null;

  return (
    <div ref={rootRef} className={cn("dd", variant === "field" && "dd--field", className)}>
      {name ? <input type="hidden" name={name} value={value} /> : null}
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        data-open={open || undefined}
        className={
          variant === "pill"
            ? "dd-pill"
            : variant === "toolbar"
              ? "tf-select"
              : cn(
                  "field field--caret",
                  label && "field-labeled",
                  stacked && "field--filled",
                  clearable && selected && "field--clearable",
                )
        }
        onClick={() => !disabled && setOpen((prev) => !prev)}
        onKeyDown={onTriggerKey}
      >
        {variant === "pill" || variant === "toolbar" ? (
          <span className="dd-copy">
            <span className="dd-value">{display}</span>
          </span>
        ) : (
          <span className="dd-copy">
            {label ? <span className="field-caption">{captionMark(label, required)}</span> : null}
            {label ? (
              stacked ? (
                <span className={cn("field-value", selected?.leading && "field-value--face")}>
                  {selected?.leading ? <SelectOptionRow item={selected} /> : selected?.label || ""}
                </span>
              ) : null
            ) : (
              <span className={cn("field-value", !selected && "field-value--mute", selected?.leading && "field-value--face")}>
                {selected?.leading ? <SelectOptionRow item={selected} /> : display}
              </span>
            )}
          </span>
        )}
        {variant === "field" && clearable && selected ? (
          <span
            role="button"
            tabIndex={-1}
            className="field-clear"
            aria-label="clear"
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              pick("");
            }}
          >
            ×
          </span>
        ) : null}
        <span className={cn("dd-caret", open && "dd-caret--open")} aria-hidden />
      </button>
      {menu}
    </div>
  );
}

/** Type freely or pick a unique suggestion. Menu portals above drawers (z-index 110). */
export function Combobox({
  value,
  onChange,
  options,
  placeholder,
  className,
  disabled,
}: {
  value: string;
  onChange: (event: { target: { value: string } }) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(value);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});

  const filtered = useMemo(() => {
    const q = value.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (item) => item.label.toLowerCase().includes(q) || item.value.toLowerCase().includes(q),
    );
  }, [options, value]);

  useLayoutEffect(() => {
    if (!open || !inputRef.current) return;
    const place = () => {
      const rect = inputRef.current!.getBoundingClientRect();
      const minWidth = Math.max(rect.width, 160);
      const estimated = Math.min(filtered.length, 8) * 44 + 16;
      const spaceBelow = window.innerHeight - rect.bottom - 12;
      const openUp = spaceBelow < Math.min(estimated, 220) && rect.top > spaceBelow;
      const left = Math.max(8, Math.min(rect.left, window.innerWidth - minWidth - 8));
      setMenuStyle({
        position: "fixed",
        left,
        width: minWidth,
        top: openUp ? undefined : rect.bottom + 6,
        bottom: openUp ? window.innerHeight - rect.top + 6 : undefined,
        zIndex: 110,
        maxHeight: 280,
        overflowY: "auto",
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, filtered.length]);

  useEffect(() => {
    if (!open) return;
    setActive(filtered.find((item) => item.value === value)?.value || filtered[0]?.value || "");
    const onPointer = (event: MouseEvent) => {
      const node = event.target as Node;
      if (rootRef.current?.contains(node) || menuRef.current?.contains(node)) return;
      setOpen(false);
    };
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, filtered, value]);

  function pick(next: string) {
    onChange({ target: { value: next } });
    setOpen(false);
    inputRef.current?.focus();
  }

  function onInputKey(event: KeyboardEvent<HTMLInputElement>) {
    if (disabled) return;
    if (!open && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      event.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    const index = Math.max(0, filtered.findIndex((item) => item.value === active));
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActive(filtered[Math.max(0, index - 1)]?.value ?? active);
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActive(filtered[Math.min(filtered.length - 1, index + 1)]?.value ?? active);
    }
    if (event.key === "Enter" && active) {
      event.preventDefault();
      pick(active);
    }
  }

  const menu =
    open && filtered.length > 0
      ? createPortal(
          <div ref={menuRef} id={listId} role="listbox" className="dd-menu" style={menuStyle}>
            {filtered.map((item, index) => (
              <button
                key={item.value || index}
                type="button"
                role="option"
                aria-selected={item.value === value}
                className="dd-item"
                data-active={item.value === active || undefined}
                data-selected={item.value === value || undefined}
                onMouseEnter={() => setActive(item.value)}
                onClick={() => pick(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={rootRef} className={cn("dd dd--field", className)}>
      <div className="field field--caret" data-open={open || undefined}>
        <input
          ref={inputRef}
          type="text"
          disabled={disabled}
          className="field-control"
          value={value}
          placeholder={placeholder}
          role="combobox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-autocomplete="list"
          onFocus={() => !disabled && setOpen(true)}
          onClick={() => !disabled && setOpen(true)}
          onKeyDown={onInputKey}
          onChange={(event) => {
            onChange({ target: { value: event.target.value } });
            setOpen(true);
          }}
        />
        <button
          type="button"
          className="dd-caret-hit"
          tabIndex={-1}
          disabled={disabled}
          aria-label="toggle"
          onClick={() => {
            if (disabled) return;
            setOpen((prev) => !prev);
            inputRef.current?.focus();
          }}
        >
          <span className={cn("dd-caret", open && "dd-caret--open")} aria-hidden />
        </button>
      </div>
      {menu}
    </div>
  );
}

export function Textarea({
  label,
  className,
  onFocus,
  onBlur,
  onChange,
  placeholder,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement> & { label?: string }) {
  const caption = label || placeholder;
  const [focused, setFocused] = useState(false);
  const [inner, setInner] = useState(() => String(props.defaultValue ?? ""));
  const current = props.value !== undefined ? String(props.value ?? "") : inner;
  const stacked = Boolean(caption) && (focused || current.length > 0);

  return (
    <label
      className={cn("field", caption && "field-labeled", stacked && "field--filled", className)}
      data-disabled={props.disabled || undefined}
      suppressHydrationWarning
    >
      {caption ? <span className="field-caption">{captionMark(caption, props.required)}</span> : null}
      <textarea
        {...props}
        className="field-control"
        placeholder={caption ? undefined : placeholder}
        suppressHydrationWarning
        onFocus={(event) => {
          setFocused(true);
          onFocus?.(event);
        }}
        onBlur={(event) => {
          setFocused(false);
          onBlur?.(event);
        }}
        onChange={(event) => {
          if (props.value === undefined) setInner(event.target.value);
          onChange?.(event);
        }}
      />
    </label>
  );
}

const SEGMENTED_ACTIVE: Record<string, string> = {
  open: "bg-[var(--positive)] !text-white",
  soft: "bg-[var(--gold)] !text-[#1c1917]",
  hard: "bg-[var(--danger)] !text-white",
};

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  disabled,
  className,
}: {
  value: T;
  options: { value: T; label: string; tone?: "open" | "soft" | "hard" }[];
  onChange: (next: T) => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <div
      role="radiogroup"
      className={cn(
        "flex flex-wrap items-center gap-1 rounded-full border border-[var(--border)] bg-[var(--surface-solid)] p-0.5",
        className,
      )}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => {
              if (disabled || option.value === value) return;
              onChange(option.value);
            }}
            className={cn(
              "rounded-full px-3 py-1 text-xs font-medium transition disabled:opacity-50",
              active
                ? SEGMENTED_ACTIVE[option.tone || ""] || "bg-[var(--gold-soft)] text-[var(--gold)]"
                : "text-mute hover:text-[var(--text)]",
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export function Button({
  tone = "primary",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { tone?: "primary" | "acid" | "ghost" | "danger" | "success" }) {
  const map = {
    primary: "btn-primary",
    acid: "btn-primary",
    ghost: "btn-ghost",
    danger: "btn-danger",
    success: "btn bg-green-600 text-white hover:bg-green-700",
  };
  return <button {...props} className={cn(map[tone], props.className)} />;
}

export type BadgeTone =
  | "neutral"
  | "live"
  | "demo"
  | "ok"
  | "warn"
  | "info"
  | "modeOpen"
  | "modeSoft"
  | "modeHard"
  | "mt4"
  | "mt5"
  | "violet"
  | "cyan"
  | "fuchsia"
  | "orange"
  | "indigo"
  | "teal";

export function Badge({
  children,
  tone = "neutral",
  className,
}: {
  children: ReactNode;
  tone?: BadgeTone;
  className?: string;
}) {
  const map: Record<BadgeTone, string> = {
    neutral: "bg-[var(--well)] text-[var(--text-body)] border-[var(--border)]",
    live: "bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-900/50 dark:text-amber-100 dark:border-amber-700",
    demo: "bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-900/50 dark:text-blue-200 dark:border-blue-700/70",
    ok: "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-green-900/50 dark:text-green-200 dark:border-green-700/70",
    warn: "bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-900/50 dark:text-rose-200 dark:border-rose-700",
    info: "bg-[var(--gold-soft)] text-[var(--gold)] border-[color:rgb(242_182_70_/_0.35)]",
    modeOpen: "bg-transparent text-[var(--positive)] border-[var(--positive)]",
    modeSoft: "bg-transparent text-[var(--gold)] border-[var(--gold)]",
    modeHard: "bg-transparent text-[var(--danger)] border-[var(--danger)]",
    mt4: "bg-violet-100 text-violet-900 border-violet-300 dark:bg-violet-900/50 dark:text-violet-100 dark:border-violet-700",
    mt5: "bg-cyan-100 text-cyan-900 border-cyan-300 dark:bg-cyan-900/50 dark:text-cyan-100 dark:border-cyan-700",
    violet: "bg-violet-100 text-violet-900 border-violet-300 dark:bg-violet-900/50 dark:text-violet-100 dark:border-violet-700",
    cyan: "bg-cyan-100 text-cyan-900 border-cyan-300 dark:bg-cyan-900/50 dark:text-cyan-100 dark:border-cyan-700",
    fuchsia: "bg-fuchsia-100 text-fuchsia-900 border-fuchsia-300 dark:bg-fuchsia-900/50 dark:text-fuchsia-100 dark:border-fuchsia-700",
    orange: "bg-orange-100 text-orange-900 border-orange-300 dark:bg-orange-900/50 dark:text-orange-100 dark:border-orange-700",
    indigo: "bg-indigo-100 text-indigo-900 border-indigo-300 dark:bg-indigo-900/50 dark:text-indigo-100 dark:border-indigo-700",
    teal: "bg-teal-100 text-teal-900 border-teal-300 dark:bg-teal-900/50 dark:text-teal-100 dark:border-teal-700",
  };
  return <span className={cn("chip", map[tone], className)}>{children}</span>;
}

const STAT_COLOR = {
  blue: "text-blue-500",
  green: "text-green-500",
  purple: "text-purple-500",
  orange: "text-orange-500",
  cyan: "text-cyan-500",
  teal: "text-teal-500",
  fuchsia: "text-fuchsia-500",
  emerald: "text-emerald-500",
};

export function TickValue({
  value,
  children,
  className,
}: {
  value: number | string | null | undefined;
  children: ReactNode;
  className?: string;
}) {
  const prev = useRef<typeof value>(undefined);
  const [flash, setFlash] = useState<{ dir: "up" | "down"; id: number } | null>(null);

  useEffect(() => {
    const before = prev.current;
    prev.current = value;
    if (before === undefined || value == null || before == null || before === value) return;
    const numeric = typeof before === "number" && typeof value === "number";
    if (numeric && before === value) return;
    const up = numeric ? value > before : String(value) > String(before);
    setFlash({ dir: up ? "up" : "down", id: Date.now() });
  }, [value]);

  return (
    <span
      key={flash?.id ?? "idle"}
      className={cn("tick-value", flash?.dir === "up" && "tick-value--up", flash?.dir === "down" && "tick-value--down", className)}
      onAnimationEnd={() => setFlash(null)}
    >
      {children}
    </span>
  );
}

function FitText({ children, className }: { children: ReactNode; className?: string }) {
  const box = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const parent = box.current;
    const node = inner.current;
    if (!parent || !node) return;
    const fit = () => {
      node.style.transform = "scale(1)";
      const pw = parent.clientWidth;
      const nw = node.scrollWidth;
      const scale = nw > pw && pw > 0 ? Math.max(0.42, pw / nw) : 1;
      node.style.transform = `scale(${scale})`;
    };
    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(parent);
    return () => ro.disconnect();
  }, [children]);

  return (
    <div ref={box} className={cn("min-w-0 overflow-hidden", className)}>
      <span ref={inner} className="inline-block origin-left whitespace-nowrap">
        {children}
      </span>
    </div>
  );
}

export function Stat({
  label,
  value,
  hint,
  color = "blue",
  tick,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  color?: keyof typeof STAT_COLOR;
  tick?: number | string | null;
}) {
  return (
    <div className="stat-card min-w-0">
      <FitText className={cn("text-4xl font-bold tabular-nums leading-none", STAT_COLOR[color])}>
        {tick === undefined ? value : <TickValue value={tick}>{value}</TickValue>}
      </FitText>
      <div className="mt-2 truncate text-sm text-[var(--mute)]">{label}</div>
      {hint && <div className="mt-1 truncate text-xs text-[var(--mute)]">{hint}</div>}
    </div>
  );
}

/** Gold slide toggle — system-wide replacement for checkboxes. */
export function Switch({
  checked,
  onChange,
  disabled,
  className,
  size = "md",
  "aria-label": ariaLabel,
  id,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  size?: "sm" | "md";
  "aria-label"?: string;
  id?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      id={id}
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      data-size={size === "sm" ? "sm" : undefined}
      className={cn("switch", checked && "switch--on", className)}
      onClick={() => !disabled && onChange(!checked)}
    >
      <span className="switch-thumb" aria-hidden />
    </button>
  );
}

export function SwitchRow({
  label,
  checked,
  onChange,
  disabled,
  className,
  size = "md",
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  size?: "sm" | "md";
}) {
  return (
    <div className={cn("switch-row", className)}>
      <span className="switch-row-label">{label}</span>
      <Switch checked={checked} onChange={onChange} disabled={disabled} size={size} />
    </div>
  );
}

export function Alert({ children, tone = "error" }: { children: ReactNode; tone?: "error" | "ok" | "warn" | "info" }) {
  const map = {
    error: "border-red-300 bg-red-50 text-red-800 dark:border-red-700/60 dark:bg-red-950/40 dark:text-red-200",
    ok: "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700/60 dark:bg-emerald-950/30 dark:text-emerald-100",
    warn: "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700/60 dark:bg-amber-950/30 dark:text-amber-100",
    info: "border-[color:rgb(242_182_70_/_0.35)] bg-[var(--gold-soft)] text-[var(--text)]",
  };
  return <div className={cn("rounded-2xl border px-3 py-2 text-sm", map[tone])}>{children}</div>;
}

/** Fixed-position hover tip (portal). Survives overflow:hidden parents. */
export function HoverTip({
  content,
  children,
  className,
}: {
  content: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<CSSProperties>({});
  const ref = useRef<HTMLSpanElement>(null);
  const tipRef = useRef<HTMLDivElement>(null);

  function place() {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const tipW = tipRef.current?.offsetWidth || 288;
    const tipH = tipRef.current?.offsetHeight || 80;
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - tipW - 8));
    const above = rect.top - tipH - 8;
    const top = above >= 8 ? above : Math.min(rect.bottom + 8, window.innerHeight - tipH - 8);
    setPos({ position: "fixed", top, left, zIndex: 90 });
  }

  useLayoutEffect(() => {
    if (!open) return;
    place();
  }, [open, content]);

  if (content == null || content === "") {
    return <span className={className}>{children}</span>;
  }

  return (
    <>
      <span
        ref={ref}
        className={cn("block cursor-default", className)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
      >
        {children}
      </span>
      {open
        ? createPortal(
            <div
              ref={tipRef}
              style={pos}
              className="pointer-events-none max-w-sm whitespace-pre-wrap rounded-xl border border-[color:rgb(242_182_70_/_0.45)] bg-[var(--surface)] px-3 py-2 font-mono text-xs leading-relaxed text-[var(--text)] shadow-[var(--shadow)]"
            >
              {content}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

export { DatePicker, DateRangePicker, formatDateDisplay, toIsoDate } from "@/components/date-field";
export type { DateRangeValue } from "@/components/date-field";
export { ToastProvider, useToast } from "@/components/toast";
export type { ToastTone } from "@/components/toast";
